import React from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { useLocation } from '@reach/router';
import CustomLinkAccordion from '$/components/ui/CustomLinkAccordion';
import { Portal } from '$/components/Portal';
import { smoothScroll } from '$/utils/scroll';
import { useHasChanged } from '$/hooks/useHasChanged';

export interface SectionTOCItem {
  id: string;
  Icon: React.FunctionComponent<{ className?: string }>;
  route: string;
  text: string;
  css?: SerializedStyles;
  showFn?: () => boolean;
}
interface AccordionProps {
  containerId?: string;
  className?: string;
  title?: string;
  sections: SectionTOCItem[];
}

const AccordionSideBar: React.FunctionComponent<AccordionProps> = ({
  sections,
  className,
  title,
  containerId = 'common-sections',
}) => {
  const [scrolling, setScrolling] = React.useState(true);
  const location = useLocation();
  const [locationHash] = React.useState(location.hash.split('?')[0]);
  const [activeSection, setActiveSection] = React.useState(() =>
    sections.findIndex((s) => s.route === locationHash)
  );

  const scrollToHash = React.useCallback(async (hash: string): Promise<void> => {
    try {
      const ele = document.getElementById(hash.substring(1));
      if (ele) {
        setScrolling(true);
        await smoothScroll(ele, 0);
        setScrolling(false);
      }
    } catch {}
  }, []);

  const onActiveSessionChange = React.useCallback(
    (sectionIndex: number, scroll = true) => {
      setActiveSection(sectionIndex);
      if (scroll && sections[sectionIndex]?.route) {
        scrollToHash(sections[sectionIndex]?.route);
      }
      if (!scrolling) {
        const [href, hash] = location.href.split('#');
        if (`#${hash}` !== sections[sectionIndex]?.route) {
          history.pushState({}, document.title, `${href}${sections[sectionIndex]?.route}`);
        }
      }
    },
    [location.href, scrollToHash, scrolling, sections]
  );

  const onScrollTopChange = React.useCallback(
    (scrollTop: number): void => {
      let nextActiveSection = activeSection;
      sections.forEach((s, index) => {
        const sectionEl = document.getElementById(s.route.substr(1));
        if (
          sectionEl &&
          scrollTop >= sectionEl.offsetTop - 10 &&
          scrollTop < sectionEl.offsetTop + sectionEl.clientHeight
        ) {
          nextActiveSection = index;
        }
      });
      const activeSectionChanged = nextActiveSection !== activeSection;
      if (activeSectionChanged) {
        onActiveSessionChange(nextActiveSection, false);
      }
    },
    [activeSection, onActiveSessionChange, sections]
  );

  const onScroll = React.useCallback(
    (e: any): void => {
      if (scrolling) return;
      const scrollTop: number = e.target.documentElement.scrollTop;
      onScrollTopChange(scrollTop);
    },
    [onScrollTopChange, scrolling]
  );

  const onLocationHashChange = useHasChanged([locationHash]);
  React.useEffect(() => {
    if (!onLocationHashChange) return;

    if (!locationHash) {
      setScrolling(false);
      onScrollTopChange(0);
      return;
    }

    if (scrolling) {
      // This is effectively onLoad()

      // We wait 2 seconds to give the page time to load any React.Suspense elements
      // that can change the height of the scroll region and location of the hashes
      window.setTimeout(() => scrollToHash(locationHash), 2000);
    } else {
      scrollToHash(locationHash);
    }
  }, [locationHash, onLocationHashChange, onScrollTopChange, scrollToHash, scrolling]);

  React.useEffect(() => {
    // Keep track of on page scrolling
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    window.addEventListener('scroll', onScroll);
    return (): void => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  return (
    <Portal containerId={`${containerId}`}>
      <div
        className='flex flex-col w-full hide-scrollbar'
        css={css`
          max-height: calc(100vh - 100px - 2rem);
          overflow: auto;
        `}
      >
        <div
          id='SideMenu'
          className={`
          ${className ?? ''}
          side-bar flex z-50 w-full pb-2e border border-color-border rounded-lg bg-white flex-col justify-start
        `}
          css={css`
            left: 0;
            top: 6.5rem;
          `}
        >
          {title && (
            <div className='flex items-center font-normal leading-none text-2xl pl-1_5e mt-1_5e mb-1e'>
              {title}
            </div>
          )}
          <CustomLinkAccordion
            className='side-bar-inner w-full'
            activeSection={activeSection}
            onActiveSectionChange={onActiveSessionChange}
            css={css`
              .inner-item {
                max-width: 260px;
              }
            `}
          >
            {sections.map(({ id, route, text, css, Icon: PageIcon, showFn }) => (
              <CustomLinkAccordion.Item
                key={text}
                to={route}
                className='item'
                id={id}
                title={text}
                icon={PageIcon}
                skipDirtyForm
                ss={css}
                showFn={showFn}
              />
            ))}
          </CustomLinkAccordion>
        </div>
      </div>
    </Portal>
  );
};

export default AccordionSideBar;
